import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Alert,
  Spin,
  Select,
  Row,
  Col,
  InputNumber,
  message,
  Avatar,
  Badge,
  Tooltip,
  Tag
} from "antd";
import {
  BookOutlined,
  SaveOutlined,
  PlusOutlined,
  UserOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useExpertService } from "../Hooks/useExpertService";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;



// Replace with this
const CreateServicePage = () => {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [serviceData, setServiceData] = useState(null);
  
  // Use the hook instead of local state
  const { 
    experts, 
    loading: hookLoading, 
    fetchExperts, 
    fetchServiceById, 
    createService, 
    updateService 
  } = useExpertService();
  
  // Expert related states
  const [selectedExpertId, setSelectedExpertId] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(null);
  
  const [defaultPricing, setDefaultPricing] = useState({
  price: 100,
  notes: ""
});
  
  const isEditMode = !!serviceId;
  // Load experts on component mount
  useEffect(() => {
    loadExperts();
  }, []);

  // Load service data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadServiceData();
    }
  }, [isEditMode]);


  // Replace with this
const loadExperts = async () => {
  try {
    await fetchExperts();
  } catch (error) {
    console.error('Error loading experts:', error);
    message.error('Failed to load experts');
  }
};



const loadServiceData = async () => {
  setPageLoading(true);
  try {
    const data = await fetchServiceById(serviceId);
    setServiceData(data);
    console.log("Loaded service data:", data);

    // Set form values
    form.setFieldsValue({
      title: data.title,
      description: data.description,
    });

    // Set expert information
    if (data.expert) {
      setSelectedExpertId(data.expert.id);
      setSelectedExpert(data.expert);
    }

   // Set default pricing
if (data.pricings && data.pricings.length > 0) {
  const defaultPricingData = data.pricings.find(p => p.isDefault) || data.pricings[0];
  
  setDefaultPricing({
    price: defaultPricingData.price,
    notes: defaultPricingData.notes || ""
  });
}

  } catch (error) {
    message.error("Failed to load service data: " + (error.message || "Unknown error"));
    navigate("/proAdmin-dashboard/expert-services");
  } finally {
    setPageLoading(false);
  }
};

const getSubmitButtonIcon = () => {
  return isEditMode ? <SaveOutlined /> : <PlusOutlined />;
};
  
  const handleExpertSelect = (expertId) => {
    setSelectedExpertId(expertId);
    // Find and store the selected expert object
    const expert = experts.find(e => e.id === expertId);
    setSelectedExpert(expert);
  };



const handleSubmit = async (values) => {
  if (!selectedExpertId) {
    message.error("Please select an expert for this service");
    return;
  }

 // Validate pricing
if (!isEditMode) {
  if (defaultPricing.price <= 0) {
    message.error("Please enter a valid price");
    return;
  }
}

  setLoading(true);
  try {
    if (isEditMode) {
      // For updates, only send title and description
      const updateData = {
        title: values.title,
        description: values.description
      };
      await updateService(serviceId, updateData);
      message.success("Service updated successfully!");
    } else {
      // For creation, send all data
     // For creation, send all data
const createData = {
  expertId: selectedExpertId,
  title: values.title,
  description: values.description,
  defaultPricing: {
    price: defaultPricing.price,
    hours: 0.5,
    notes: defaultPricing.notes
  }
};
      
      await createService(createData);

    }

    setTimeout(() => {
      navigate("/proAdmin-dashboard/expert-services");
    }, 1500);
  } catch (error) {
    console.error("Submit error:", error);
    const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
    message.error(`Failed to ${isEditMode ? 'update' : 'create'} service: ${errorMessage}`);
  } finally {
    setLoading(false);
  }
};

  const handleBack = () => {
  navigate("/proAdmin-dashboard/expert-services");
};

  const getHeaderTitle = () => {
    return isEditMode ? "Edit Service" : "Create New Service";
  };

  const getSubmitButtonText = () => {
    return isEditMode ? "Update Service" : "Create Service";
  };

  // Custom render function for expert dropdown options
  const renderExpertOption = (expert) => {
    const hasServices = expert.expertServiceCount > 0;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
        <Avatar 
          src={expert.profileImage} 
          size="large" 
          style={{ marginRight: 12 }}
        >
          {expert.firstName?.charAt(0)}{expert.lastName?.charAt(0)}
        </Avatar>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            {expert.firstName} {expert.lastName}
            {hasServices && (
              <Tooltip title={`This expert already has ${expert.expertServiceCount} service(s)`}>
                <Badge 
                  count={expert.expertServiceCount} 
                  size="small" 
                  style={{ marginLeft: 8, backgroundColor: '#52c41a' }}
                />
              </Tooltip>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {expert.expertCode} • {expert.email}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {expert.yearsOfExperience || 0} years experience
            <span style={{ marginLeft: 12 }}>
              <BookOutlined style={{ marginRight: 4 }} />
              {expert.expertServiceCount || 0} services
            </span>
          </div>
          {expert.selectedCategoryTags && expert.selectedCategoryTags.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {expert.selectedCategoryTags.slice(0, 3).map((tag, index) => (
                <span key={index} style={{ 
                  display: 'inline-block', 
                  backgroundColor: '#f0f0f0', 
                  borderRadius: '4px', 
                  padding: '2px 6px', 
                  margin: '2px', 
                  fontSize: '10px' 
                }}>
                  {tag.name}
                </span>
              ))}
              {expert.selectedCategoryTags.length > 3 && (
                <span style={{ 
                  display: 'inline-block', 
                  backgroundColor: '#f0f0f0', 
                  borderRadius: '4px', 
                  padding: '2px 6px', 
                  margin: '2px', 
                  fontSize: '10px' 
                }}>
                  +{expert.selectedCategoryTags.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Custom render function for selected expert
  const renderExpertSelected = (expertId) => {
    const expert = experts.find(e => e.id === expertId);
    if (!expert) return null;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Avatar 
          src={expert.profileImage} 
          size="small" 
          style={{ marginRight: 8 }}
        >
          {expert.firstName?.charAt(0)}{expert.lastName?.charAt(0)}
        </Avatar>
        <span>
          {expert.firstName} {expert.lastName} ({expert.expertCode})
        </span>
      </div>
    );
  };

  if (pageLoading) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <Spin size="large" />
        <Title level={4} style={{ marginTop: 16 }}>Loading service data...</Title>
      </div>
    );
  }

  return (
    <Card
      className="card-professional"
      title={
        <div>
          <BookOutlined /> {getHeaderTitle()}
          {isEditMode && serviceData && (
            <span style={{ marginLeft: 8, fontSize: '14px', color: '#9A6B0E' }}>
              ID: {serviceId}
            </span>
          )}
        </div>
      }
      extra={
        <Button
          type="default"
          onClick={handleBack}
        >
          Back to Services
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>



        {/* Service Creation/Edit Form */}
        <Card style={{ background: "#F6F3ECff" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark="optional"
          >

            {/* Expert Selection Section */}
            <Form.Item
              label={
                <Space>
                  <TeamOutlined />
                  <span>Service Expert</span>
                  <Tooltip title="Select an expert who will provide this service">
                    <InfoCircleOutlined style={{ color: '#9A6B0E' }} />
                  </Tooltip>
                </Space>
              }
              required
            >
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Text type="secondary">
                  Select an expert for this service
                </Text>

          
<Card size="small">
  {hookLoading ? (
    <Spin size="small" />
  ) : experts.length > 0 ? (
    <Select
      placeholder="Select an expert"
      value={selectedExpertId}
      onChange={handleExpertSelect}
      style={{ width: '100%' }}
      showSearch
      optionFilterProp="children"
      disabled={isEditMode} // Disable in edit mode
      filterOption={(input, option) => {
        const expert = experts.find(e => e.id === option.value);
        if (!expert) return false;
        const searchText = `${expert.firstName} ${expert.lastName} ${expert.expertCode} ${expert.email}`.toLowerCase();
        return searchText.includes(input.toLowerCase());
      }}
      optionLabelProp="label"
    >
      {experts.map(expert => (
        <Option 
          key={expert.id} 
          value={expert.id}
          label={`${expert.firstName} ${expert.lastName} (${expert.expertCode})`}
        >
          {renderExpertOption(expert)}
        </Option>
      ))}
    </Select>
  ) : (
    <Alert
      message="No experts available"
      description="There are no existing experts in the system."
      type="info"
      size="small"
    />
  )}
</Card>


                {/* Selected Expert Display */}
               {selectedExpertId && (
  <Card size="small" style={{ borderColor: '#52c41a' }}>
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space>
        <UserOutlined style={{ color: '#52c41a' }} />
        <Text strong>Selected Expert:</Text>
        {renderExpertSelected(selectedExpertId)}
        {isEditMode && (
          <Tag color="gold" style={{ marginLeft: 8 }}>Locked</Tag>
        )}
      </Space>
      
      {/* Show notification if expert already has services */}
      {selectedExpert && selectedExpert.expertServiceCount > 0 && (
        <Alert
          message={`This expert already has ${selectedExpert.expertServiceCount} service(s)`}
          description="Consider reviewing existing services to avoid duplication and ensure this new service offers unique value."
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          size="small"
        />
      )}
      
      {/* Add note about expert being locked in edit mode */}
      {isEditMode && (
        <Alert
          message="Expert Assignment Locked"
          description="The expert assigned to this service cannot be changed in edit mode. If you need to assign a different expert, please create a new service."
          type="warning"
          showIcon
          size="small"
        />
      )}
    </Space>
  </Card>
)}              </Space>
            </Form.Item>

            {/* Service Title */}
            <Form.Item
              name="title"
              label="Service Title"
              rules={[
                { required: true, message: "Please enter service title" },
                { min: 3, message: "Title must be at least 3 characters" },
                { max: 100, message: "Title must not exceed 100 characters" }
              ]}
            >
              <Input
                placeholder="Enter service title (e.g., 'Advanced Career Coaching')"
                size="large"
              />
            </Form.Item>

            {/* Service Description */}
            <Form.Item
              name="description"
              label="Service Description"
              rules={[
                { required: true, message: "Please enter service description" },
                { min: 10, message: "Description must be at least 10 characters" },
                { max: 1000, message: "Description must not exceed 1000 characters" }
              ]}
            >
              <TextArea
                rows={6}
                placeholder="Describe what this service includes, the benefits, and who it's for..."
                showCount
                maxLength={1000}
              />
            </Form.Item>

{/* Default Pricing Section - Only show in create mode */}
{!isEditMode && (
  <Form.Item
    label={
      <Space>
        <DollarOutlined />
        <span>Default Pricing</span>
        <Tooltip title="Set the default price for a 30-minute session">
          <InfoCircleOutlined style={{ color: '#9A6B0E' }} />
        </Tooltip>
      </Space>
    }
    required
  >
    <Card size="small">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Price ($)" required>
            <InputNumber
              placeholder="Price for 30 minutes"
              min={1}
              precision={2}
              style={{ width: '100%' }}
              value={defaultPricing.price}
              onChange={(value) => setDefaultPricing(prev => ({ ...prev, price: value || 0 }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Notes">
            <Input
              placeholder="e.g., For new clients"
              value={defaultPricing.notes}
              onChange={(e) => setDefaultPricing(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
        <InfoCircleOutlined style={{ marginRight: 4 }} />
        This is unit pricing for a 30-minute session
      </div>
    </Card>
  </Form.Item>
)}


            {/* Form Actions */}
            <Form.Item>
              <Space>
               <Button
  type="primary"
  htmlType="submit"
  loading={loading}
  size="large"
  icon={getSubmitButtonIcon()}
  disabled={!isEditMode && !selectedExpertId}
>
  {getSubmitButtonText()}
</Button>
                <Button
                  onClick={handleBack}
                  size="large"
                >
                  Cancel
                </Button>
              </Space>
             {!isEditMode && !selectedExpertId && (
  <div style={{ marginTop: 8 }}>
    <Alert
      message="Expert Required"
      description="Please select an expert before submitting"
      type="error"
      showIcon
    />
  </div>
)}
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </Card>
  );
};

export default CreateServicePage;